<template>
  <v-card
    class="course-card d-flex flex-column"
    outlined
    hover
    @click="onOpen"
  >
    <div class="course-card__media">
      <v-img
        :src="imageSrc"
        height="168"
        contain
        eager
        @error="onImageError"
      ></v-img>
      <span
        v-if="categoryLabel"
        class="course-card__badge"
        :style="badgeStyle"
      >
        <v-icon x-small :color="badge.color">{{ badge.icon }}</v-icon>
        {{ categoryLabel }}
      </span>
    </div>

    <div class="course-card__body d-flex flex-column flex-grow-1">
      <h3 class="course-card__title">{{ displayTitle }}</h3>
      <p v-if="displayDescription" class="course-card__desc">
        {{ displayDescription }}
      </p>

      <hr v-if="hasExplanation" class="course-card__divider course-card__divider--short" />

      <div class="course-card__explain-slot">
        <p v-if="hasExplanation" class="course-card__explain">
          {{ course.explanation }}
        </p>
      </div>

      <hr v-if="hasMeta" class="course-card__divider" />

      <div v-if="hasMeta" class="course-card__meta">
        <span v-if="durationLabel">
          <v-icon x-small color="#64748b">mdi-clock-outline</v-icon>
          {{ durationLabel }}
        </span>
        <span v-if="levelLabel">
          <v-icon x-small color="#64748b">mdi-signal-cellular-2</v-icon>
          {{ levelLabel }}
        </span>
        <span v-if="modeLabel">
          <v-icon x-small color="#64748b">{{ modeIcon }}</v-icon>
          {{ modeLabel }}
        </span>
      </div>

      <v-btn
        block
        depressed
        :outlined="outlined"
        color="primary"
        class="course-card__cta"
        @click.stop="onOpen"
      >
        {{ actionLabel }}
        <v-icon right small>mdi-arrow-right</v-icon>
      </v-btn>
    </div>
  </v-card>
</template>

<script>
const BADGE_PALETTE = [
  { bg: "#dbeafe", color: "#1d4ed8", icon: "mdi-chart-bar" },
  { bg: "#dcfce7", color: "#15803d", icon: "mdi-bullhorn-outline" },
  { bg: "#ede9fe", color: "#6d28d9", icon: "mdi-cube-outline" },
  { bg: "#ffedd5", color: "#c2410c", icon: "mdi-cog-outline" },
  { bg: "#fce7f3", color: "#be185d", icon: "mdi-palette-outline" },
  { bg: "#e0f2fe", color: "#0369a1", icon: "mdi-laptop" },
]

const ICON_BY_KEYWORD = [
  { re: /market|seo|buscad/i, icon: "mdi-bullhorn-outline" },
  { re: /dato|data|analy|python|estad/i, icon: "mdi-chart-bar" },
  { re: /3d|video|game|juego|unity/i, icon: "mdi-gamepad-variant-outline" },
  { re: /gest|manag|crm|proyec|admin/i, icon: "mdi-cog-outline" },
  { re: /design|diseñ|art/i, icon: "mdi-palette-outline" },
  { re: /wood|mader|furnit/i, icon: "mdi-saw-blade" },
]

function stripHtml(value) {
  return String(value || "")
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim()
}

function hashString(value) {
  let hash = 0
  const text = String(value || "")
  for (let i = 0; i < text.length; i++) {
    hash = (hash << 5) - hash + text.charCodeAt(i)
    hash |= 0
  }
  return Math.abs(hash)
}

export default {
  name: "CourseCard",
  props: {
    course: { type: Object, required: true },
    to: { type: String, default: "" },
    actionLabel: { type: String, default: "View course" },
    outlined: { type: Boolean, default: false },
  },
  data: () => ({
    imageFailed: false,
  }),
  computed: {
    imageSrc() {
      const file =
        this.imageFailed || !(this.course && this.course.image)
          ? "default.jpg"
          : this.course.image
      return process.env.VUE_APP_API_ENDPOINT + "images/courses/" + file
    },
    displayTitle() {
      const title = (this.course && this.course.title) || ""
      return title.charAt(0).toUpperCase() + title.slice(1)
    },
    displayDescription() {
      const text = stripHtml(this.course && this.course.description)
      if (!text) return ""
      if (text.length <= 160) return text
      return text.slice(0, 157).trim() + "…"
    },
    categoryLabel() {
      const raw = this.course && (this.course.category || this.course.topic)
      if (!raw) return ""
      return String(raw)
    },
    badge() {
      const label = this.categoryLabel
      const fromKeyword = ICON_BY_KEYWORD.find((item) => item.re.test(label))
      const palette = BADGE_PALETTE[hashString(label) % BADGE_PALETTE.length]
      return {
        bg: palette.bg,
        color: palette.color,
        icon: (fromKeyword && fromKeyword.icon) || palette.icon,
      }
    },
    badgeStyle() {
      return {
        backgroundColor: this.badge.bg,
        color: this.badge.color,
      }
    },
    durationLabel() {
      const raw = this.course && this.course.duration
      if (raw == null || String(raw).trim() === "") return ""
      const text = String(raw).trim()
      if (/[a-zA-Z]/.test(text)) return text
      return text + " hours"
    },
    levelLabel() {
      const raw = this.course && this.course.eqf_nqf_level
      if (raw == null || String(raw).trim() === "") return ""
      const match = String(raw).match(/(\d+)/)
      if (!match) return String(raw)
      const level = Number(match[1])
      if (level <= 3) return "Beginner"
      if (level <= 5) return "Intermediate"
      return "Advanced"
    },
    modeRaw() {
      return (
        (this.course &&
          (this.course.course_type_name || this.course.type || this.course.mode)) ||
        ""
      )
    },
    modeLabel() {
      const raw = String(this.modeRaw)
      if (!raw) return ""
      if (/on.?site|presenc|face.?to.?face|classroom/i.test(raw)) return "In-person"
      if (/blend|hybrid|mix/i.test(raw)) return "Blended"
      if (/online|e-?learn|distance|remote|virtual/i.test(raw)) return "Online"
      return raw
    },
    modeIcon() {
      if (/in-person|presenc|on.?site|face|classroom/i.test(this.modeLabel + this.modeRaw)) {
        return "mdi-account-group-outline"
      }
      if (/blend|hybrid/i.test(this.modeLabel + this.modeRaw)) {
        return "mdi-swap-horizontal"
      }
      return "mdi-laptop"
    },
    hasMeta() {
      return !!(this.durationLabel || this.levelLabel || this.modeLabel)
    },
    hasExplanation() {
      return !!(this.course && this.course.explanation)
    },
  },
  methods: {
    onImageError() {
      this.imageFailed = true
    },
    onOpen() {
      if (this.$listeners.view) {
        this.$emit("view", this.course)
        return
      }
      if (this.to) {
        this.$router.push(this.to)
      }
    },
  },
}
</script>

<style scoped>
.course-card {
  flex: 1 1 auto;
  width: 100%;
  height: 100% !important;
  border-radius: 16px !important;
  border-color: #e8edf3 !important;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06) !important;
  overflow: hidden;
  cursor: pointer;
}
.course-card__media {
  position: relative;
  z-index: 0;
  flex-shrink: 0;
  background: #f1f5f9;
}
.course-card__badge {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
}
.course-card__body {
  position: relative;
  z-index: 1;
  padding: 16px 16px 14px;
  background: #fff;
  box-shadow: 0 -6px 12px rgba(15, 23, 42, 0.12);
}
.course-card__title {
  flex-shrink: 0;
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
  color: #0f172a;
  margin: 0 0 8px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.course-card__desc {
  flex-shrink: 0;
  font-size: 13.5px;
  line-height: 1.45;
  color: #475569;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}
.course-card__explain-slot {
  flex: 1 1 auto;
  min-height: 0;
}
.course-card__explain {
  font-size: 12.5px;
  line-height: 1.35;
  color: #64748b;
  font-style: italic;
  margin: 0;
}
.course-card__divider {
  flex-shrink: 0;
  border: 0;
  border-top: 1px solid #e8edf3;
  margin: 12px 0;
}
.course-card__divider--short {
  width: 32px;
  margin-left: auto;
  margin-right: auto;
}
.course-card__meta {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
  color: #64748b;
  font-size: 13px;
}
.course-card__meta span {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.course-card__cta {
  flex-shrink: 0;
  margin-top: auto !important;
  text-transform: none !important;
  letter-spacing: 0;
  font-weight: 600;
  border-radius: 10px !important;
  height: 40px !important;
  min-height: 40px !important;
  max-height: 40px !important;
}
</style>
